import { useEffect, useMemo, useRef, useState } from "react";
import { formatClock } from "../../../shared/watching/line.ts";
import { useWorkspaceText } from "../components/useWorkspaceJson";
import { Markdown } from "../markdown";
import { currentNote, parseVideoNotes } from "./videoNotes";

/**
 * The notes of the video above it: one button per note, the one the video has reached open.
 * A missing file is the ordinary state before the agent has written any, and shows nothing.
 */
export function NotesBar({
	sessionId,
	source,
	revision,
	seconds,
	busy,
	onSeek,
	onAdd,
}: {
	sessionId: string;
	source: string;
	/** A change reads the file again. */
	revision: number;
	/** Where the video stands. */
	seconds: number;
	/** A run is on, so a note cannot be asked for. */
	busy: boolean;
	onSeek: (seconds: number) => void;
	/** Resolves to null once asked, or to why it was not. */
	onAdd: () => Promise<string | null>;
}) {
	const { data } = useWorkspaceText(sessionId, source, revision);
	/** Kept across a refetch, so the list does not blink each time the agent finishes. */
	const [text, setText] = useState("");
	const [refused, setRefused] = useState<string | null>(null);
	const open = useRef<HTMLLIElement>(null);

	useEffect(() => {
		if (data !== null) setText(data);
	}, [data]);

	const notes = useMemo(() => parseVideoNotes(text), [text]);
	const current = currentNote(notes, seconds);

	useEffect(() => {
		open.current?.scrollIntoView({ block: "nearest" });
	}, [current]);

	return (
		<section className="video-notes" aria-label="视频笔记">
			{notes.length > 0 && (
				<ol>
					{notes.map((note, i) => (
						<li key={note.seconds} ref={i === current ? open : undefined} className={i === current ? "current" : undefined}>
							<button type="button" aria-current={i === current ? "true" : undefined} title="跳转到此时刻" onClick={() => onSeek(note.seconds)}>
								<span className="timestamp">{formatClock(note.seconds)}</span>
								{note.starred && <span className="video-note-star" title="手动记录">★</span>}
								<span className="video-note-title">{note.title}</span>
							</button>
							{i === current && note.body && <Markdown text={note.body} />}
						</li>
					))}
				</ol>
			)}
			<div className="video-notes-foot">
				<button type="button" disabled={busy} onClick={() => void onAdd().then(setRefused)}>
					＋记笔记
				</button>
				{refused && <span className="video-note">{refused}</span>}
			</div>
		</section>
	);
}
